import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";

import { cn } from "@/lib/utils";

function TooltipProvider({
    delay = 0,
    ...props
}: TooltipPrimitive.Provider.Props) {
    return (
        <TooltipPrimitive.Provider
            data-slot="tooltip-provider"
            delay={delay}
            {...props}
        />
    );
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
    return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

type TooltipVariant = "default" | "rich";

function TooltipContent({
    className,
    side = "top",
    sideOffset = 4,
    align = "center",
    alignOffset = 0,
    variant = "default",
    showArrow = true,
    children,
    ...props
}: TooltipPrimitive.Popup.Props &
    Pick<
        TooltipPrimitive.Positioner.Props,
        "align" | "alignOffset" | "side" | "sideOffset"
    > & { variant?: TooltipVariant; showArrow?: boolean }) {
    const isRich = variant === "rich";
    // MUI modals use z-index 1300; portal to body and match MUI's 1500
    // tooltip layer to escape transformed dialog stacking contexts.
    const bodyContainer =
        typeof document !== "undefined" ? document.body : undefined;
    return (
        <TooltipPrimitive.Portal container={bodyContainer}>
            <TooltipPrimitive.Positioner
                align={align}
                alignOffset={alignOffset}
                side={side}
                sideOffset={sideOffset}
                className="isolate z-[1500]"
            >
                <TooltipPrimitive.Popup
                    data-slot="tooltip-content"
                    data-variant={variant}
                    className={cn(
                        "data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 inline-flex w-fit origin-(--transform-origin) has-data-[slot=kbd]:pr-1.5 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm",
                        isRich
                            ? "bg-popover text-popover-foreground ring-foreground/10 max-w-[280px] flex-col items-start gap-1 rounded-lg px-3.5 py-2.5 text-sm shadow-md ring-1"
                            : "bg-foreground text-background max-w-xs items-center gap-1.5 rounded-md px-3 py-1.5 text-xs",
                        className
                    )}
                    {...props}
                >
                    {children}
                    {showArrow && (
                        <TooltipPrimitive.Arrow
                            data-slot="tooltip-arrow"
                            className={cn(
                                "size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5",
                                isRich
                                    ? "bg-popover fill-popover ring-foreground/10 ring-1"
                                    : "bg-foreground fill-foreground"
                            )}
                        />
                    )}
                </TooltipPrimitive.Popup>
            </TooltipPrimitive.Positioner>
        </TooltipPrimitive.Portal>
    );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
